import type { ReactNode } from 'react';

import Breadcrumbs, { type Crumb } from '~/components/BreadCrumbs';
import Image from '~/components/Image';

type Avatar = { alt: string; src: string };

type Props = {
  actions?: ReactNode;
  avatar?: Avatar;
  breadcrumbs: Crumb[];
  children?: ReactNode;
  hasFilters?: boolean;
  stackActions?: boolean;
  subtitle?: string;
  title: ReactNode;
};

export default function PageHeader({
  actions,
  avatar,
  breadcrumbs,
  children,
  hasFilters,
  stackActions,
  subtitle,
  title
}: Props) {
  const hasBreadcrumbs = breadcrumbs.length > 0;

  return (
    <>
      <div
        className={`content-width flex w-full flex-col gap-1 pt-4 ${hasBreadcrumbs ? '' : 'md:pt-8'} ${hasFilters ? 'pb-4' : 'pb-2'}`}
      >
        {hasBreadcrumbs && (
          <div className="hidden md:flex">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
        )}
        <div
          className={`flex flex-col ${children ? 'gap-5' : 'gap-3'} ${
            stackActions ? '' : 'md:flex-row md:items-start md:gap-4'
          }`}
        >
          <div className={`flex flex-1 flex-col ${avatar ? 'gap-4' : 'gap-1'}`}>
            <Heading avatar={avatar} subtitle={subtitle} title={title} />
            {children}
          </div>
          {actions}
        </div>
      </div>
      <div className="content-width w-full">
        <hr className="border-grey-divide" />
      </div>
    </>
  );
}

function Heading({
  avatar,
  subtitle,
  title
}: Pick<Props, 'avatar' | 'subtitle' | 'title'>) {
  const heading = (
    <div className="flex flex-col">
      <h1 data-cy="page-title" className="text-title-md leading-8">
        {title}
      </h1>
      {subtitle ? <p className="text-sm text-light-grey">{subtitle}</p> : null}
    </div>
  );

  if (!avatar) return heading;

  return (
    <div className="flex items-center gap-4 md:mt-4">
      <Image
        className="size-16 shrink-0 rounded-full object-cover"
        alt={avatar.alt}
        src={avatar.src}
        pad={false}
        quality={100}
        loading="lazy"
        responsiveOptions={{ xs: { width: 200 } }}
      />
      {heading}
    </div>
  );
}
